import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { getCustomerStatusSummary } from 'services/customerStatusService';
import {
  getBasesTerritories,
  getFlagsFromTerritory,
} from 'services/baseTerritoryService';
import { getInstallBases } from 'services/basesServices';
import type { BaseFlags } from 'services/baseTerritoryService';
import type {
  BaseTerritoryData,
  InstallBaseData,
} from 'services/basesServices';
import {
  customerStatusFailure,
  customerStatusInvalidate,
  customerStatusRequest,
  customerStatusSuccess,
} from 'store/actions/customerStatus-actions';
import type { CustomerStatusState } from 'store/reducers/customerStatus-reducer';
import {
  BlockReason,
  UnverifiedBlock,
  resolveBlockDestination,
  resolveBlockReasons,
} from 'utils/blockReasons';
import type { BlockDestination } from 'utils/blockReasons';

export const CUSTOMER_STATUS_TTL_MS = 60_000;

const SITE_CODE_KEY = '@UltragazEmpresarial:customerSiteCode';

export const getSiteCode = (): string =>
  localStorage.getItem(SITE_CODE_KEY) ?? '';

type StoreShape = { customerStatusReducer: CustomerStatusState };

export type UseCustomerStatus = {
  status: CustomerStatusState['status'];
  reasons: BlockReason[];
  unverified: UnverifiedBlock[];
  summary: CustomerStatusState['summary'];
  territoryByBase: CustomerStatusState['territoryByBase'];
  flagsByBase: Record<string, BaseFlags>;
  isLoading: boolean;
  hasError: boolean;
  load: () => Promise<void>;
  refresh: () => Promise<BlockReason[]>;
  invalidate: () => void;
  destination: BlockDestination;
};

let inFlight: Promise<void> | null = null;

export const useCustomerStatus = (): UseCustomerStatus => {
  const dispatch = useDispatch();
  const state = useSelector((store: StoreShape) => store.customerStatusReducer);

  const store = useStore<StoreShape>();
  const readState = useCallback(
    (): CustomerStatusState => store.getState().customerStatusReducer,
    [store]
  );

  const fetchAll = useCallback(
    async (siteCode: string): Promise<void> => {
      dispatch(customerStatusRequest(siteCode));

      const [summaryResult, bases] = await Promise.all([
        getCustomerStatusSummary(siteCode),
        getInstallBases({ includeInactive: true }),
      ]);

      const baseCodes = ((bases as InstallBaseData[]) ?? []).map(
        (base) => base.name
      );

      const { territoryByBase, failed } = await getBasesTerritories(baseCodes);

      const summaryFailed = !summaryResult.ok;
      const basesFailed =
        baseCodes.length > 0 && failed.length === baseCodes.length;

      if (summaryFailed && (basesFailed || baseCodes.length === 0)) {
        dispatch(customerStatusFailure(siteCode));
        return;
      }

      dispatch(
        customerStatusSuccess({
          siteCode,
          summary: summaryResult.ok ? summaryResult.data : {},
          summaryFailed,
          territoryByBase,
          failedBases: failed,
          fetchedAt: Date.now(),
        })
      );
    },
    [dispatch]
  );

  const run = useCallback(
    async (force: boolean): Promise<void> => {
      const siteCode = getSiteCode();
      if (!siteCode) return;

      const current = readState();
      const isFresh =
        !force &&
        current.status === 'ready' &&
        current.siteCode === siteCode &&
        current.fetchedAt !== null &&
        Date.now() - current.fetchedAt < CUSTOMER_STATUS_TTL_MS;

      if (isFresh) return;

      if (inFlight && !force) {
        await inFlight;
        return;
      }

      inFlight = fetchAll(siteCode).finally(() => {
        inFlight = null;
      });

      await inFlight;
    },
    [fetchAll, readState]
  );

  const load = useCallback((): Promise<void> => run(false), [run]);

  const refresh = useCallback(async (): Promise<BlockReason[]> => {
    await run(true);

    const current = readState();
    const currentFlags = Object.keys(current.territoryByBase).reduce<
      Record<string, BaseFlags>
    >(
      (acc, baseCode) => ({
        ...acc,
        [baseCode]: getFlagsFromTerritory(current.territoryByBase[baseCode]),
      }),
      {}
    );

    return resolveBlockReasons({
      summary: current.summary ?? undefined,
      flagsByBase: currentFlags,
      failedBases: current.failedBases,
      summaryFailed: current.summaryFailed || current.status === 'error',
    }).reasons;
  }, [run, readState]);

  const invalidate = useCallback((): void => {
    dispatch(customerStatusInvalidate());
  }, [dispatch]);

  const flagsByBase = useMemo(
    () =>
      Object.keys(state.territoryByBase).reduce<Record<string, BaseFlags>>(
        (acc, baseCode) => ({
          ...acc,
          [baseCode]: getFlagsFromTerritory(state.territoryByBase[baseCode]),
        }),
        {}
      ),
    [state.territoryByBase]
  );

  const { reasons, unverified } = useMemo(
    () =>
      resolveBlockReasons({
        summary: state.summary ?? undefined,
        flagsByBase,
        failedBases: state.failedBases,
        summaryFailed: state.summaryFailed || state.status === 'error',
      }),
    [
      state.summary,
      flagsByBase,
      state.failedBases,
      state.summaryFailed,
      state.status,
    ]
  );

  return {
    status: state.status,
    reasons,
    unverified,
    summary: state.summary,
    territoryByBase: state.territoryByBase,
    flagsByBase,
    isLoading: state.status === 'loading' || state.status === 'idle',
    hasError: state.status === 'error',
    load,
    refresh,
    invalidate,
    destination: resolveBlockDestination(reasons),
  };
};

export const useCustomerStatusOnEnter = (): UseCustomerStatus => {
  const customerStatus = useCustomerStatus();
  const { load } = customerStatus;

  useEffect(() => {
    load();
  }, [load]);

  return customerStatus;
};

export default useCustomerStatus;
